import { Injectable } from '@angular/core';

import { Odds as BposOdds, SystemType, TaxationModel } from '@bpos';
import { BetModel, BetType as ParticipantBetType } from '@bpos/common';
import { Taxation } from '@bpos/v2/taxation';
import { Bet, BetPlacementSlipTaxRequest, BetPlacementSlipTaxResponse, Slip } from '@bpos/v2/taxation/place-bet';
import { ApiService } from '@frontend/sports/common/api-utils';
import { BetslipConfig, TaxationConfig } from '@frontend/sports/common/client-config-data-access';
import { BposOddsConverterService, Odds, emptyOdds } from '@frontend/sports/odds/feature';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';

import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2WinParticipantPick } from '../../../core/picks/betslip-v2-win-participant-pick';
import { ParticipantPickType, PickType, PriceType } from '../../../core/picks/pick-models';
import { BetslipV2HorseRaceOptionMarketPick } from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { ISlipPick } from '../../bet-generation/models';
import { ComboSlipType, SingleSlipType, SlipType, SystemSlipType } from '../../bet-generation/slip-type';
import { IBetslipSummary, ISlipSummary } from '../../summary/models';
import { convertSystem } from '../../system-bet/utils/system-type.helper';
import { ITaxationResult } from '../models';

@Injectable({ providedIn: 'root' })
export default class BetslipTaxationService {
    private readonly cutOffFuncDecimal: (val: Decimal, decimals?: number) => Decimal;

    private lastTaxationPostSuccessful: boolean;
    private lastTaxationPayloadSignature: string;
    private lastTaxationPost: Observable<ITaxationResult[] | null>;

    constructor(
        private user: UserService,
        private betslipConfig: BetslipConfig,
        private api: ApiService,
        private taxationConfig: TaxationConfig,
        private bposOddsConverterService: BposOddsConverterService,
        private userSettingsService: SyncUserSettingsService,
    ) {
        this.cutOffFuncDecimal = (val: Decimal, decimals: number = 2) =>
            val.toDecimalPlaces(decimals, betslipConfig.roundValues ? Decimal.ROUND_HALF_UP : Decimal.ROUND_DOWN);
    }

    getTaxation(betslipSummary: IBetslipSummary): Observable<ITaxationResult[] | null> {
        if (betslipSummary.slipSummaries.length === 0) {
            // When no slips
            return of(null);
        }

        return this.getServiceTaxationIfEnabled(betslipSummary).pipe(
            map((serviceTaxation) => {
                if (betslipSummary.slipSummaries.every((ss) => ss.possibleWinnings == null)) {
                    if (
                        (this.user.isAuthenticated && this.userSettingsService.configSettings.winningsTaxRate > 0) ||
                        this.isServiceTaxationEnabled(betslipSummary)
                    ) {
                        return <ITaxationResult[]>[
                            {
                                isPlaceholderTax: true,
                            },
                        ];
                    }

                    // When no possible winnings ( when stake is empty or all picks doesn't have fixed odds )
                    return null;
                }
                const taxRate = this.getTaxRate(betslipSummary.possibleWinnings);

                if (taxRate.greaterThan(0)) {
                    return this.getFlatTax(
                        betslipSummary,
                        taxRate,
                        this.userSettingsService.configSettings.winningsTaxThreshold,
                        this.userSettingsService.isTurnoverTaxApplied,
                    );
                }

                return serviceTaxation;
            }),
        );
    }

    getTaxationForAmount(amount: Decimal): Decimal | null {
        const taxRate = this.getTaxRate(amount);
        if (taxRate.greaterThan(0)) {
            return this.userSettingsService.isTurnoverTaxApplied
                ? this.cutOffFuncDecimal(amount.times(taxRate).toDecimalPlaces(4), 0)
                : this.roundNumber(amount.times(taxRate));
        }

        return null;
    }

    private getFlatTax(
        betslipSummary: IBetslipSummary,
        taxRate: Decimal,
        winningsTaxThreshold: number,
        isTurnoverTax: boolean,
    ): ITaxationResult[] | null {
        return betslipSummary.slipSummaries.reduce((taxationResult: ITaxationResult[], slipSummary: ISlipSummary): ITaxationResult[] => {
            const slipTax = this.getSlipFlatTaxAmount(slipSummary, taxRate, winningsTaxThreshold, isTurnoverTax);
            if (slipTax !== null) {
                return [...taxationResult, { amount: slipTax, rate: taxRate }];
            }

            return [...taxationResult];
        }, []);
    }

    getPossibleWinnings(summary: IBetslipSummary): Decimal | null {
        return (
            this.userSettingsService.isTurnoverTaxApplied &&
            !!summary.slipSummaries[0].rewardTokenModifications?.freeBet?.amount &&
            !!summary.taxation
                ? summary.possibleWinnings?.minus(Number(summary.slipSummaries[0].rewardTokenModifications?.freeBet?.amount))
                : summary.possibleWinnings
        ) as Decimal;
    }

    /**
     * Round a taxation sum
     * TODO: Extract this in a service, because we have same method in the summary builder.
     * Update Monzer ignored during v3 cleanup (please verify this and the other function, and other built in functions within odds converter)
     * @param val
     * @private
     */
    private roundNumber(val: Decimal): Decimal {
        const round = this.betslipConfig.roundValues;

        return round ? val.times(100).round().div(100) : val.times(100).floor().div(100);
    }

    getSlipFlatTaxAmount(slipSummary: ISlipSummary, taxRate: Decimal | null, winningsTaxThreshold: number, isTurnoverTax: boolean): Decimal | null {
        // Create dictionary of the picks of faster access.
        const slipPicks = slipSummary.slip.slipPicks;
        if (slipSummary.betSummaries.every((b) => b.possibleWinnings == null)) {
            return null;
        }

        return slipSummary.betSummaries.reduce((tax, betSummary) => {
            const betPicks = betSummary.bet.legs.map((l) => slipPicks[l.pickId.toString()]);
            if (betPicks.every((p) => p.pick.isVirtual)) {
                // When bet is from virtual picks we don't tax them.
                // Currently, we can't combine virtual with non-virtual picks, so we can check directly all slip picks,
                // but in theory if we can combine then we need to check the bet legs, when in system we may have bets from virtual and bets with mixed legs.
                return tax; // No tax added when all picks in bet are virtual.
            }
            if (betSummary.possibleWinnings == null) {
                // when no possible winnings no tax.
                return tax;
            }

            // We need to add the freebet amount (if any) to the taxable amount
            let taxableAmount: Decimal =
                isTurnoverTax && !!slipSummary.rewardTokenModifications?.freeBet?.amount && winningsTaxThreshold < Number(betSummary.possibleWinnings)
                    ? betSummary.possibleWinnings
                    : betSummary.possibleWinnings.plus(slipSummary.rewardTokenModifications?.freeBet?.amount || 0);

            if (isTurnoverTax && winningsTaxThreshold >= Number(betSummary.possibleWinnings) && slipSummary.slip.type instanceof SystemSlipType) {
                taxableAmount = new Decimal(0);
            }

            return tax.plus(
                isTurnoverTax
                    ? this.cutOffFuncDecimal(taxableAmount.times(taxRate || 0).toDecimalPlaces(4), 0)
                    : this.roundNumber(taxableAmount.times(taxRate || 0)),
            );
        }, new Decimal(0));
    }

    /* Tax calculated on server */
    private getTaxationFromService(betslipSummary: IBetslipSummary): Observable<ITaxationResult[] | null> {
        const request = this.createTaxationRequest(betslipSummary.slipSummaries);
        const signature = JSON.stringify(request);

        //This was added to prevent mutliple calculation posts, ideally it wouldn't be needed but too many observables to keep track off, hence this hack.
        if (this.lastTaxationPayloadSignature === signature && this.lastTaxationPostSuccessful) {
            return this.lastTaxationPost;
        }

        this.lastTaxationPayloadSignature = signature;

        this.lastTaxationPost = this.api
            .post<BetPlacementSlipTaxResponse>('taxation/calculate', request, {
                showSpinner: false,
            })
            .pipe(
                map((response) => {
                    if (!response?.taxations?.length) {
                        return null;
                    }
                    const taxations = response.taxations.reduce(
                        (acm: ITaxationResult[], cur: Taxation): ITaxationResult[] => [
                            ...acm,
                            { amount: new Decimal(cur.value || 0), isPlaceholderTax: !cur.value },
                        ],
                        [],
                    );

                    return taxations;
                }),
                catchError(() => of(null as ITaxationResult[] | null)),
                tap((result) => (this.lastTaxationPostSuccessful = !!result)),
                shareReplay(1),
            );

        return this.lastTaxationPost;
    }

    /**
     * Check if the sports needs a taxation calculation from backend service TaxationService.
     *
     * @param betslip
     * @private
     */
    private getServiceTaxationIfEnabled(betslipSummary: IBetslipSummary): Observable<ITaxationResult[] | null> {
        if (!this.isServiceTaxationEnabled(betslipSummary)) {
            return of(null);
        }
        const picks = betslipSummary.slipSummaries.flatMap((ss) => Object.values(ss.slip.slipPicks));

        const taxedPicks = picks.filter((p) => this.taxationConfig.taxableSports.includes(p.pick.sportId));

        if (taxedPicks.every((t) => t.isStartingPrice)) {
            return of(
                betslipSummary.slipSummaries.map((_) => ({
                    isPlaceholderTax: true,
                })),
            );
        }

        return this.getTaxationFromService(betslipSummary);
    }

    private isServiceTaxationEnabled(betslipSummary: IBetslipSummary) {
        if (!this.taxationConfig.isTaxationEnabled) {
            return false;
        }
        const picks = betslipSummary.slipSummaries.flatMap((ss) => Object.values(ss.slip.slipPicks));

        return picks.some((p) => this.taxationConfig.taxableSports.includes(p.pick.sportId));
    }

    /**
     * Check if there is a flat taxation rate on the client.
     *
     * @private
     */
    getTaxRate(possibleWinning: Decimal | null): Decimal {
        const possibleWin = possibleWinning ?? 0;

        return this.user.isAuthenticated &&
            this.userSettingsService.configSettings.winningsTaxRate > 0 &&
            Number(possibleWin) > Number(this.userSettingsService.configSettings.winningsTaxThreshold)
            ? new Decimal(this.userSettingsService.configSettings.winningsTaxRate)
            : new Decimal(0);
    }

    private createTaxationRequest(slipSummaries: ISlipSummary[]): BetPlacementSlipTaxRequest {
        return {
            slips: slipSummaries.map((summary, index) => this.createTaxationSlip(summary, index)),
            taxModel: null as unknown as TaxationModel, // Taxation model will be assigned on server.
        };
    }

    private createTaxationSlip(summary: ISlipSummary, index: number): Slip {
        const [bankers, normal] = Object.values(summary.slip.slipPicks).reduce(
            (aggr, si) => {
                if (si.isBanker) {
                    aggr[0].push(si);
                } else {
                    aggr[1].push(si);
                }

                return aggr;
            },
            [[], []] as [ISlipPick[], ISlipPick[]],
        );

        const stake = summary.rewardTokenModifications?.freeBet?.amount?.toNumber() ?? (summary.slip.stake || 0);
        const systemType = this.systemConverter(summary.slip.type);

        return {
            systemType: systemType ? systemType : undefined,
            bankerBets: bankers.map((p) => this.createTaxationBet(p)),
            bets: normal.map((p) => this.createTaxationBet(p)),
            key: index,
            stake,
        };
    }

    private createTaxationBet(pick: ISlipPick): Bet {
        const bet = {
            odds: pick.price.type === PriceType.Fixed ? this.createTaxationOddsV4(pick.price.nativeOdds) : this.createTaxationOddsV4(emptyOdds),
            picks: pick.priceIds.map((priceId) => ({ pickId: priceId })),
            placeTerm: pick.eachWayInfo?.placeTerms,
            betType: this.getParticipantBetType(pick),
            betModel: this.getBetModel(pick),
            sportId: pick.pick.sportId,
            partitionId: pick.pick.partitionId,
        } as Bet; // Odds and place terms are optional, we need to update typings generator to able to set optional types

        if (!bet.placeTerm && pick.pick instanceof BetslipV2WinParticipantPick) {
            const participantPick = pick.pick;
            const placeTerms = participantPick.market?.placeTerms;

            if (placeTerms) {
                bet.placeTerm = {
                    denominator: placeTerms.denominator,
                    numerator: placeTerms.numerator,
                };
            }
        }

        return bet;
    }

    private createTaxationOddsV4(odds: Odds): BposOdds {
        return this.bposOddsConverterService.convertToBposOdds(odds);
    }

    private systemConverter(slipType: SlipType): SystemType | null {
        if (slipType instanceof SingleSlipType || slipType instanceof ComboSlipType) {
            return null;
        } else {
            return convertSystem(slipType.key);
        }
    }

    private getParticipantBetType(slipPick: ISlipPick): ParticipantBetType | undefined {
        if (BetslipV2ParticipantPick.isPick(slipPick.pick)) {
            switch (slipPick.pick.id.pickType) {
                case ParticipantPickType.Win:
                    return slipPick.eachWayInfo ? ParticipantBetType.EachWay : ParticipantBetType.Win;
                case ParticipantPickType.Forecast:
                    return ParticipantBetType.Forecast;
                case ParticipantPickType.CombinationForecast:
                    return ParticipantBetType.CombinationForecast;
                case ParticipantPickType.Tricast:
                    return ParticipantBetType.Tricast;
                case ParticipantPickType.CombinationTricast:
                    return ParticipantBetType.CombinationTricast;
            }
        } else if (
            BetslipV2HorseRaceOptionMarketPick.isPick(slipPick.pick) &&
            (slipPick.pick.pickType === ParticipantPickType.Win || slipPick.pick.pickType === ParticipantPickType.Derived)
        ) {
            return ParticipantBetType.Win;
        }

        return;
    }

    private getBetModel(slipPick: ISlipPick): BetModel {
        switch (slipPick.pick.id.getPickType()) {
            case PickType.V1Pick:
                return BetModel.Result;
            case PickType.V2ParticipantPick:
                return BetModel.Participant;
            case PickType.V2OptionMarketPick:
            case PickType.BetBuilderPick:
            case PickType.V2OptionMarketXcastPick:
            case PickType.GroupedPicks:
                return BetModel.Option;
        }
    }
}
